import React, { useState } from 'react';
import { BASE_URL } from '../../../config/config';
import './ReviewModal.css';

// Star component for rating
const StarIcon = ({ filled, onClick, onMouseEnter, onMouseLeave }) => (
  <svg
    className={`star-icon ${filled ? 'filled' : ''}`}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

export const ReviewModal = ({
  isOpen,
  onClose,
  userId,
  eventId,
  eventName = 'this event',
  onSubmitSuccess,
  onSubmitError,
  type = 'event' // 'event' or 'organizer'
}) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('Event Report');
  const [comment, setComment] = useState('');
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const MAX_COMMENT_LENGTH = 500;
  const remainingChars = MAX_COMMENT_LENGTH - comment.length;

  const handleSubmit = async () => {
    if (rating === 0) {
      setError('Please select a rating');
      return;
    }
    if (!title.trim()) {
      setError('Please enter a title');
      return;
    }

    setError('');
    setIsSubmitting(true);

    try {
      const response = await fetch(`${BASE_URL}/reviews/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          eventId,
          rating,
          title: "Event Report",
          comment: comment.trim(),
        }),
      });

      const data = await response.json();

      if (data.result || response.ok) {
        onSubmitSuccess?.(data);
        handleClose();
      } else {
        setError(data.message || 'Failed to submit review');
        onSubmitError?.(data);
      }
    } catch (err) {
      console.error('Error submitting review:', err);
      setError('An error occurred. Please try again.');
      onSubmitError?.(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setRating(0);
    setHoverRating(0);
    setTitle('');
    setComment('');
    setConsent(false);
    setError('');
    onClose();
  };

  const handleCommentChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_COMMENT_LENGTH) {
      setComment(value);
    }
  };

  if (!isOpen) return null;

  const displayRating = hoverRating || rating;

  return (
    <div className="review-modal-overlay" onClick={handleClose}>
      <div className="review-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="review-modal-close" onClick={handleClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="review-modal-header">
          <h2>Share your experience</h2>
          <p>How would you rate {eventName}?</p>
        </div>

        {/* Star Rating Section */}
        <div className="review-rating-section">
          <div className="star-rating">
            {[1, 2, 3, 4, 5].map((star) => (
              <StarIcon
                key={star}
                filled={star <= displayRating}
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
              />
            ))}
          </div>
          {/* <span className="rating-text">
            {rating === 0 ? 'Select rating' : 
             rating === 1 ? 'Poor' :
             rating === 2 ? 'Fair' :
             rating === 3 ? 'Good' :
             rating === 4 ? 'Very Good' : 'Excellent'}
          </span> */}
        </div>

        {/* Title Input */}
        

        {/* Comment Textarea */}
        <div className="review-input-group">
          <label htmlFor="review-comment">Description </label>
          <textarea
            id="review-comment"
            placeholder="Tell us more about your experience..."
            value={comment}
            onChange={handleCommentChange}
            className="review-textarea"
            rows={4}
          />
          <span className="char-counter">{remainingChars}/{MAX_COMMENT_LENGTH} characters left</span>
        </div>

        {/* Error Message */}
        {error && <div className="review-error">{error}</div>}

        {/* Consent Checkbox */}
        <label className="review-consent">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
          />
          <span>I consent to Hobi contacting me based on my feedback</span>
        </label>

        {/* Action Buttons */}
        <div className="review-modal-actions">
          <button 
            className="review-btn-secondary" 
            onClick={handleClose}
            type="button"
          >
            Close
          </button>
          <button 
            className="review-btn-primary" 
            onClick={handleSubmit}
            disabled={isSubmitting || rating === 0}
            type="button"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </div>
      </div>
    </div>
  );
};
